import { useCallback, useEffect, useRef, useState } from "react";
import {
	GRID_DETAIL_PANEL_TOGGLE_FIELD,
	GridFilterModel,
	GridPaginationModel,
	GridRowId,
	GridSortModel,
	GridInitialState,
} from "@mui/x-data-grid-pro";
import { AsyncState, useAsyncState } from "src/utils/async/asyncState.ts";
import { logError } from "src/errorHandling/errorLogging.ts";
import { DEFAULT_PAGINATION_MODEL, setColumnVisibility } from "src/components/common/dataGrid/utils.ts";
import type { ControlledAsyncFormCrudDataGridProps } from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid.tsx";
import {
	dataGridPersistentStateProps,
	DEFAULT_GRID_INITIAL_STATE,
} from "src/components/common/dataGrid/dataGridStateStorage.ts";
import {
	ServerSideDataModelRequest,
	ServerSideDataModelRequest_FilterModel,
	ServerSideDataModelRequest_SortItem,
} from "src/api/generated/common/dataGrids/serverSideDataModelRequest.ts";
import {
	ServerSideDataModelResult,
	ServerSideDataModelResult_DataGridData,
} from "src/api/generated/common/dataGrids/serverSideDataModelResult.ts";
import { useForwardedRef } from "src/utils/useForwardedRef.ts";
import { useMaybeControlledState } from "src/utils/useMaybeControlledState.ts";
import {
	UseDataGridModelCommonParams,
	UseDataGridModelCommonReturn,
	UseDataGridModelGridCommonProps,
	usePersistentDataGridId,
} from "src/components/common/dataGrid/gridModel/dataGridModelCommon.ts";
import { setRefreshRefValue } from "src/utils/useRefreshRef.ts";
import { expectNonFileResult } from "src/components/common/dataGrid/gridModel/serverSideDataModelUtils.ts";
import { DataGridCsvExportButton } from "src/components/common/dataGrid/csvDownload/DataGridCsvExportButton.tsx";
import { usePartiallyStoredState } from "src/utils/usePartiallyStoredState.ts";
import {
	applyDataGridRowUpdates,
	DataGridRowUpdateHandler,
} from "src/components/common/dataGrid/gridModel/dataGridRowUpdates.ts";

export interface UseServerSideDataGridModelParams<
	RowData extends object,
	TParams extends ParamsWithoutDataModelRequest<unknown>,
> extends UseDataGridModelCommonParams<RowData, TParams> {
	fetchData: (params: ParamsWithDataModelRequest<TParams>) => Promise<ServerSideDataModelResult<RowData>>;
	initialRowSelection?: (rows: RowData[]) => RowData | undefined;
	usePagination?: boolean;
	hideDetailPanelToggle?: (rows: RowData[]) => boolean;
}

export interface UseServerSideDataGridModelReturn<
	RowData extends object,
	TParams extends ParamsWithoutDataModelRequest<unknown>,
> extends UseDataGridModelCommonReturn<RowData, TParams> {
	dataGridProps: UseServerSideDataGridModelGridProps<RowData>;
	setLocalRow: (index: number, setter: (currentValue: RowData) => RowData) => void;
	currentDataModelRequest: ServerSideDataModelRequest;
}

export type UseServerSideDataGridModelGridProps<RowData extends object> = UseDataGridModelGridCommonProps<RowData> &
	Required<
		Pick<
			ControlledAsyncFormCrudDataGridProps<RowData>,
			| "rowCount"
			| "pagination"
			| "sortingMode"
			| "paginationMode"
			| "filterMode"
			| "onSortModelChange"
			| "onPaginationModelChange"
			| "onFilterModelChange"
			| "filterModel"
			| "hideFooter"
		>
	> & {
		updateRows: DataGridRowUpdateHandler;
	};

type ParamsWithoutDataModelRequest<T> = Omit<T, "dataModelRequest">;

type ParamsWithDataModelRequest<TParams> = TParams & {
	dataModelRequest: ServerSideDataModelRequest;
};

export type ServerSideDataGridModelRefreshFunc<
	FetchFunc extends (params: ParamsWithDataModelRequest<unknown>) => Promise<ServerSideDataModelResult<unknown>>,
> = UseServerSideDataGridModelReturn<object, ServerSideDataGridModelFetchParams<FetchFunc>>["refreshData"];

export type ServerSideDataGridModelFetchParams<
	FetchFunc extends (params: ParamsWithDataModelRequest<unknown>) => Promise<ServerSideDataModelResult<unknown>>,
> = Required<ParamsWithoutDataModelRequest<Parameters<FetchFunc>[0]>>;

export const useServerSideDataGridModel = <RowData extends object, TParams extends ParamsWithoutDataModelRequest<any>>({
	gridId,
	customFunctionUiActionView,
	customFunctionUiActionObjectId,
	refreshRef,
	getRowId,
	fetchData,
	initialParams,
	storedParams = [],
	apiRef: apiRefProp,
	usePagination = true,
	selectedRows: selectedRowsProp,
	defaultSelectedRows = [],
	onSelectionChanged: setSelectedRowsProp,
	selectFirstRowOnLoad,
	initialRowSelection,
	hideDetailPanelToggle: hideDetailPanelToggleFunc,
	defaultGridState = DEFAULT_GRID_INITIAL_STATE,
}: UseServerSideDataGridModelParams<RowData, TParams>): UseServerSideDataGridModelReturn<RowData, TParams> => {
	const apiRef = useForwardedRef(apiRefProp);
	const persistentGridId = usePersistentDataGridId(gridId);
	const [currentParams, setCurrentParams] = usePartiallyStoredState({
		initialState: initialParams,
		storedKeys: storedParams,
		key: "data-grid-params--" + gridId,
	});
	const [dataAsync, setDataAsync, setDataImmediate] = useAsyncState<ServerSideDataModelResult_DataGridData<RowData>>(
		{},
	);
	const rowsRef = useRef<RowData[]>([]);
	const initialRowSelectionRef = useRef(initialRowSelection);
	const latestRefreshIdRef = useRef(0);
	rowsRef.current = dataAsync.data?.rows ?? [];

	const { initialState: initialGridState, onStateChange: persistGridState } = dataGridPersistentStateProps(
		persistentGridId,
		defaultGridState,
	);

	const [sortModel, setSortModel] = useState<GridSortModel>(initialGridState.sorting?.sortModel ?? []);

	const [filterModel, setFilterModel] = useState<GridFilterModel>(
		initialGridState.filter?.filterModel ?? DEFAULT_FILTER_MODEL,
	);

	const [paginationModel, setPaginationModel] = useState<GridPaginationModel | undefined>({
		pageSize: initialGridState?.pagination?.paginationModel?.pageSize ?? DEFAULT_PAGINATION_MODEL.pageSize,
		page: 0,
	});

	const currentDataModelRequest: ServerSideDataModelRequest = {
		resultType: "DATA",
		sortModel: mapSortModelToBackend(sortModel),
		paginationModel: usePagination ? paginationModel : undefined,
		filterModel: mapFilterModelToBackend(filterModel),
	};

	const [selectedRows, setSelectedRows] = useMaybeControlledState({
		controlledValue: selectedRowsProp,
		onChange: setSelectedRowsProp,
		defaultValue: defaultSelectedRows,
	});
	const onlySelectedRow = selectedRows.length === 1 ? selectedRows[0] : undefined;

	const onRowSelectionChanged = (selectedRowIds: GridRowId[]) => {
		const selectedRows = rowsRef.current.filter((row) => selectedRowIds.includes(getRowId(row)));
		setSelectedRows(selectedRows);
	};

	const updateRows: DataGridRowUpdateHandler = useCallback((updates) => {
		setDataImmediate((currentData) => {
			if (!currentData) return currentData;
			const updatedRows = applyDataGridRowUpdates(currentData.rows, updates, getRowId);
			rowsRef.current = updatedRows;
			return {
				...currentData,
				rows: updatedRows,
			};
		});
	}, [getRowId, setDataImmediate]);

	const handleSortModelChange = async (model: GridSortModel) => {
		setSortModel(model);
		await refreshDataWithParams({
			newSortModel: model,
		});
	};

	const handlePaginationModelChange = async (model: GridPaginationModel) => {
		setPaginationModel(model);
		await refreshDataWithParams({
			newPaginationModel: model,
		});
	};

	const handleFilterModelChange = async (model: GridFilterModel) => {
		setFilterModel(model);
		await refreshDataWithParams({
			newFilterModel: model,
		});
	};

	const refreshDataWithParams = async ({
		newSortModel = sortModel,
		newPaginationModel = paginationModel,
		newFilterModel = filterModel,
		newParams = currentParams,
	}: {
		newSortModel?: GridSortModel;
		newPaginationModel?: GridPaginationModel | undefined;
		newFilterModel?: GridFilterModel;
		newParams?: TParams;
	}) => {
		const refreshId = ++latestRefreshIdRef.current;
		try {
			const dataModelRequest: ServerSideDataModelRequest = {
				resultType: "DATA",
				sortModel: mapSortModelToBackend(newSortModel),
				paginationModel: usePagination ? newPaginationModel : undefined,
				filterModel: mapFilterModelToBackend(newFilterModel),
			};
			const paramsWithDataGridRequest = {
				...newParams,
				dataModelRequest: dataModelRequest,
			};
			const newData = await setDataAsync(async () => {
				const result = await fetchData(paramsWithDataGridRequest);
				return expectNonFileResult(result);
			});
			if (refreshId !== latestRefreshIdRef.current) return newData.rows;

			rowsRef.current = newData.rows;
			const initialRow = initialRowSelectionRef.current?.(newData.rows);
			initialRowSelectionRef.current = undefined;

			if (initialRow && selectedRows.length === 0) {
				setSelectedRows([initialRow]);
			} else if (selectFirstRowOnLoad && selectedRows.length === 0) {
				setSelectedRows(newData.rows.slice(0, 1));
			} else {
				const newSelectedRows = newData.rows.filter((row) =>
					selectedRows.some((selectedRow) => getRowId(selectedRow) === getRowId(row)),
				);
				setSelectedRows(newSelectedRows);
			}

			return newData.rows;
		} catch {
			// Errors in grid state may cause persistent errors, so we reset the state on error.
			setFilterModel(DEFAULT_FILTER_MODEL);
			setSortModel([]);
			persistGridState(DEFAULT_GRID_INITIAL_STATE);
			return [];
		}
	};

	const refreshData = (params: Partial<TParams> = {}) => {
		setCurrentParams((prev) => {
			return {
				...prev,
				...params,
			};
		});
		return refreshDataWithParams({
			newParams: {
				...currentParams,
				...params,
			},
		});
	};

	const setLocalRow = (index: number, setter: (currentValue: RowData) => RowData) => {
		setDataImmediate((currentData) => {
			if (!currentData) {
				return currentData;
			}
			const currentRow = currentData.rows[index];
			if (currentRow === undefined) {
				return currentData;
			}
			const newRows = [...currentData.rows];
			newRows[index] = setter(currentRow);
			return {
				...currentData,
				rows: newRows,
			};
		});
	};

	if (refreshRef) setRefreshRefValue(refreshRef, (params) => refreshData(params));

	const getApi = useCallback(() => {
		if (!apiRef.current) {
			throw new Error("DataGrid API ref is not set");
		}
		return apiRef.current;
	}, [apiRef]);

	useEffect(() => {
		refreshData().catch(logError);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		dataGridProps: {
			customFunctionUiActionView,
			customFunctionUiActionObjectId,
			apiRef: apiRef,
			pagination: true,
			hideFooter: !usePagination,
			sortingMode: "server",
			paginationMode: "server",
			filterMode: "server",
			onSortModelChange: handleSortModelChange,
			onPaginationModelChange: handlePaginationModelChange,
			onFilterModelChange: handleFilterModelChange,
			filterModel: filterModel,
			initialState: maybeHideDetailPanelToggle(initialGridState, dataAsync, hideDetailPanelToggleFunc),
			onStateChange: persistGridState,
			refreshData: () => refreshData(),
			updateRows: updateRows,
			selectedRows: selectedRows.map(getRowId),
			onRowSelectionChanged: onRowSelectionChanged,
			getRowId: getRowId,
			rowCount: dataAsync.data?.totalRowCount ?? 0,
			gridId: persistentGridId,
			rowsAsync: {
				...dataAsync,
				data: dataAsync.data?.rows,
				loading: dataAsync.loading,
			},
			actionBarMenuComponents2: [
				<DataGridCsvExportButton
					key={"dataGridDefaultExportDataButton"}
					dataModel={{
						type: "serverSide",
						fetchData: async (dataModelRequest) =>
							fetchData({
								...currentParams,
								dataModelRequest: {
									...currentDataModelRequest,
									...dataModelRequest,
								},
							}),
					}}
					getApi={getApi}
				/>,
			],
		},
		currentParams: currentParams,
		refreshData: refreshData,
		setLocalRow: setLocalRow,
		selectedRows: selectedRows,
		setSelectedRows: setSelectedRows,
		onlySelectedRow: onlySelectedRow,
		getApi: getApi,
		currentDataModelRequest: currentDataModelRequest,
	};
};

const mapSortModelToBackend = (sortModel: GridSortModel): ServerSideDataModelRequest_SortItem[] =>
	sortModel.map((sortItem) => ({
		field: sortItem.field,
		sort: sortItem.sort === "desc" ? "DESC" : "ASC",
	}));

const mapFilterModelToBackend = (filterModel: GridFilterModel): ServerSideDataModelRequest_FilterModel => ({
	...filterModel,
	items: filterModel.items.map((item) => ({
		...item,
		value: item.value ?? null,
	})),
	logicOperator: filterModel.logicOperator === "or" ? "OR" : "AND",
});

const DEFAULT_FILTER_MODEL: GridFilterModel = {
	items: [],
};

function maybeHideDetailPanelToggle<RowData>(
	gridState: GridInitialState,
	dataAsync: AsyncState<ServerSideDataModelResult_DataGridData<RowData>>,
	hideDetailPanelToggleFunc: ((rows: RowData[]) => boolean) | undefined,
): GridInitialState {
	if (!hideDetailPanelToggleFunc || dataAsync.data === undefined) {
		return gridState;
	}
	const hideDetailPanelToggle = hideDetailPanelToggleFunc(dataAsync.data.rows);

	return setColumnVisibility(gridState, GRID_DETAIL_PANEL_TOGGLE_FIELD, !hideDetailPanelToggle);
}
